<template>
  <div v-if="shouldDisplayBlock" class="bg-surface-default-low">
    <h2 class="heading-2 mb-24 px-24 text-center">
      {{
        isSmartphone
          ? i18n(translations.title)
          : i18n(translations.subtitleDynamic, {
              nb_points: categories?.checkpoints?.length,
            })
      }}
    </h2>

    <div class="relative" :class="gradientClasses">
      <ul
        ref="listContainer"
        class="flex gap-24 overflow-x-auto px-64 scrollbar-none"
      >
        <li
          v-for="checkpoint in categories?.checkpoints"
          :key="checkpoint.text.id"
          ref="listItem"
          class="flex w-160 shrink-0 flex-col items-center gap-6 text-center"
        >
          <component :is="getIconComponent(checkpoint.icon)" size="20" />
          <span>{{ i18n(checkpoint.text) }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRuntimeConfig } from '#imports'
import { computed, useTemplateRef } from 'vue'

import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { categories as CHECKPOINTS_CATEGORIES_DATA } from '@backmarket/nuxt-layer-cms/BaseAnimatedProductCheckpoints/categories'
import { getIconComponent } from '@backmarket/nuxt-layer-cms/BaseAnimatedProductCheckpoints/utils'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { tw } from '@backmarket/utils/string/tw'

import { ProductCategory } from '@/scopes/product/constants'

import { CHECKPOINTS_CATEGORIES_MAPPING } from './CheckpointsCarousel.constants'
import translations from './CheckpointsCarousel.translations'
import { useCarouselImpression } from './composables/useCarouselImpression'

const { countryCode } = useMarketplace().market
const config = useRuntimeConfig()
const i18n = useI18n()
const { trackCarouselImpression } = useTracking()

const props = defineProps<{
  categoryId: GetProductResponse['tracking']['categoryId']
}>()

const gradientClasses = tw`md:after:content-[''] md:after:absolute md:after:inset-0 md:after:pointer-events-none md:after:bg-[linear-gradient(90deg,#fff_0%,rgba(255,255,255,0)_49.81%,#fff_100%)]`

const categories = computed(() => {
  const checkpointsCategoriesMapping =
    CHECKPOINTS_CATEGORIES_MAPPING[props.categoryId]

  return checkpointsCategoriesMapping
    ? CHECKPOINTS_CATEGORIES_DATA[checkpointsCategoriesMapping]
    : null
})

const shouldDisplayBlock = computed(() => {
  return (
    categories.value &&
    categories.value.checkpoints &&
    config.public.FF_ENABLE_VERIFIED_REFURBISHED_LINK_PP?.includes(countryCode)
  )
})

const listContainer = useTemplateRef<HTMLUListElement>('listContainer')
const listItem = useTemplateRef<HTMLLIElement[]>('listItem')

const { onImpression } = useCarouselImpression(listContainer, listItem)

const isSmartphone = computed(() => {
  return props.categoryId === ProductCategory.SMARTPHONE
})

onImpression(({ currentSlide }) => {
  trackCarouselImpression({
    name: 'carousel_interaction',
    zone: 'verified_refurbished_carousel',
    blockPosition: currentSlide,
    currentSlide,
    value: categories.value?.checkpoints[currentSlide].text.id,
  })
})
</script>
